/**
 * lib/acting/bearer — the credential half of the acting context, as a LEAF.
 *
 * ⛔ WHY THIS IS A SEPARATE FILE FROM `context.ts`, AND WHY IT MUST STAY ONE.
 *
 * `context.ts` is the React read surface. It imports `use-session`, and that
 * pulls a real cycle behind it:
 *
 *   acting/context → use-session → auth-context → onboarding-reset
 *                  → query-client → acting/context
 *
 * That cycle already cost this project eleven suites once: an eager derivation
 * at `query-client` module scope threw `Cannot access '__vite_ssr_import_5__'
 * before initialization`, and the fix was to derive lazily (see the import-cycle
 * note in `query-client.ts`). `lib/api/client.ts` is imported by very nearly
 * every module in the app, so joining it to that cycle would widen the blast
 * radius of the same trap to the whole frontend — and it would drag
 * `query-client` and `auth-context` into the module graph of every test that
 * only wanted to check which bearer went on the wire.
 *
 * This file therefore imports NOTHING but the two zustand stores and the
 * supabase client, all three of which are leaves (zustand and supabase-js and
 * nothing else). It is safe for `api()` to import.
 *
 * `context.ts` imports and re-exports everything here, so there is exactly one
 * definition of a credential and one bearer resolver. Do not copy either back.
 */

import { useFirmActStore } from '../firm-act-store';
import { useImpersonationStore } from '../impersonation-store';
import { supabase } from '../supabase';

/** WHO the operator is acting on behalf of. Lives here, not in `context.ts`,
 *  because `lastKnownActAsSubject()` below returns one and the sign-out /
 *  user-swap seam must be able to reach that WITHOUT importing the React
 *  surface — see this file's header on the import cycle. */
export interface ActingSubject {
  readonly tenantId: string | null;
  /** Display name. Under `self` this is null, because copy says "you", not a name. */
  readonly displayName: string | null;
  readonly kind: 'me' | 'client' | 'impersonated-user';
}

/** Which token `api()` sends by default, and what that token can and cannot do. */
export type CredentialKind = 'session' | 'act-as' | 'impersonation';

/**
 * 'expired' is NEVER folded into 'active'.
 *
 * That distinction is the whole reason `lib/api/client.ts` throws
 * ACT_AS_EXPIRED before `fetch` instead of falling back to the operator's own
 * session: falling back would route a possibly-WRITE call into the FIRM's tenant
 * carrying the CLIENT's data. Any derivation that collapses the two re-opens a
 * wrong-tenant write.
 */
export type CredentialStatus = 'none' | 'active' | 'expired';

export interface ActingCredential {
  readonly kind: CredentialKind;
  readonly status: CredentialStatus;
  /** Hard expiry of a MINTED token. Null for a Supabase session, which
   *  auto-refreshes and has no expiry the UI should ever act on. */
  readonly expiresAt: string | null;
  /**
   * FALSE for act-as and impersonation. This is the C2 wall, and until now it
   * was tribal knowledge that cost a P0.
   *
   * `mintFirmActToken` (backend/src/lib/firm-act-token.ts) never stamps
   * `mfa_challenge_at`, and neither does the impersonation mint. So a
   * step-up-gated handler 403s STEP_UP_REQUIRED on a minted token, the global
   * step-up modal challenges, mints a fresh SUPABASE token, and replays on the
   * UNCHANGED minted token, which 403s again. The operator reads an infinite MFA
   * loop as "the button does nothing"; `POST /v1/assets` was built and stayed
   * dead for exactly this reason.
   *
   * Every step-up-gated call must pin to the operator session when this is
   * false. Six asset writes now do (AST-03) as six separate per-call remedies
   * for what is a single per-CREDENTIAL fact; this field is that fact, stated
   * once.
   */
  readonly canSatisfyStepUp: boolean;
}

/** Alive or not, from an ISO expiry and a clock. An unparseable stamp is dead. */
export function livenessOf(expiresAt: string, now: number): 'active' | 'expired' {
  const exp = Date.parse(expiresAt);
  if (Number.isNaN(exp) || exp <= now) return 'expired';
  return 'active';
}

/**
 * Build a credential.
 *
 * `canSatisfyStepUp` is ONE expression rather than a literal repeated per
 * posture, so it cannot drift branch by branch the way the eight hand-rolled
 * role sets did. A minted token can never satisfy step-up because the mints
 * never stamp `mfa_challenge_at`; a Supabase session can, once it actually
 * exists.
 */
export function credentialOf(
  kind: CredentialKind,
  status: CredentialStatus,
  expiresAt: string | null,
): ActingCredential {
  return {
    kind,
    status,
    expiresAt,
    canSatisfyStepUp: kind === 'session' && status === 'active',
  };
}

/** What `api()` needs to put on the wire, and what it must refuse to. */
export interface ContextBearer {
  readonly credential: ActingCredential;
  /**
   * The bearer, or null.
   *
   * NULL FOR AN EXPIRED MINTED TOKEN, AND NEVER THE OPERATOR'S SESSION IN ITS
   * PLACE. Handing back the session there is the wrong-tenant write: the call
   * was addressed to the client's tenant and would land in the firm's. The
   * caller reads `credential.status` and fails loudly.
   */
  readonly token: string | null;
}

/**
 * The bearer for the CURRENT posture, without deciding what to do about it.
 *
 * Two things this deliberately does NOT do, both of which stay with `api()`:
 *
 *   1. IT DOES NOT CLEAR EITHER STORE. `activeFirmActToken()` and
 *      `activeImpersonationToken()` have this shape but clear on expiry as a
 *      side effect of being read. Whichever of the two pairs a context derives
 *      from changes behaviour, so this reads the non-clearing pair and leaves
 *      clearing to the one place that already owns it.
 *   2. IT DOES NOT THROW. `api()` keeps ownership of the ACT_AS_EXPIRED throw
 *      and its `/admin/client-book` redirect verbatim, because
 *      `use-act-as-renewal.ts` keys on that exact code as proof the request
 *      never left the browser and is therefore safe to retry.
 *
 * ⚠ ONE DIFFERENCE FROM THE FOUR-ACCESSOR VERSION, WHICH `api()` NEUTRALISES ON
 * PURPOSE AND WHICH ANY OTHER CALLER MUST DECIDE FOR ITSELF: `api()` used to let
 * an EXPIRED IMPERSONATION fall through to the operator's own session, because
 * `activeImpersonationToken()` clears itself and returns null and the expired
 * branch below it only ever covered act-as. This reports impersonation expiry
 * honestly instead of erasing it. `lib/api/beneficiaries.ts` already takes the
 * honest answer and refuses (`beneficiaries.test.ts` — "expired impersonation:
 * refuses rather than downgrading to the operator session"); `api()` still
 * reproduces the old fall-through, because changing that for every call in the
 * app is a security change that needs its own tranche and its own test rather
 * than a silent ride-along on a refactor.
 *
 * The session read is injectable so `api()` can keep using its own coalescing
 * reader, which dedupes the ~8 concurrent dashboard queries onto one
 * `getSession()`, and so tests need not stand up supabase-js. Note that a minted
 * token short-circuits before the session is ever read, exactly as before: an
 * act-as call has never paid for a `getSession()` and still does not.
 */
export async function bearerTokenForContext(
  readSessionToken: () => Promise<string | undefined | null> = defaultReadSessionToken,
): Promise<ContextBearer> {
  const now = Date.now();

  const impersonation = useImpersonationStore.getState().active;
  if (impersonation) {
    const status = livenessOf(impersonation.expiresAt, now);
    return {
      credential: credentialOf('impersonation', status, impersonation.expiresAt),
      token: status === 'active' ? impersonation.jwt : null,
    };
  }

  const firmAct = useFirmActStore.getState().active;
  if (firmAct) {
    const status = livenessOf(firmAct.expiresAt, now);
    return {
      credential: credentialOf('act-as', status, firmAct.expiresAt),
      token: status === 'active' ? firmAct.jwt : null,
    };
  }

  const token = (await readSessionToken()) ?? null;
  return {
    credential: credentialOf('session', token ? 'active' : 'none', null),
    token,
  };
}

async function defaultReadSessionToken(): Promise<string | undefined> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token;
}
